import * as React from "react";

import { extractTopicReferences } from "@/shared/domain/skills/topicReference";
import { useOrgTopicNames } from "@/shared/domain/topics/hooks";

export interface RelevantTopics {
  /** Every topic name in the org, or [] until the catalog resolves. */
  topicNames: string[];
  /** `relevant_topics` for a body, or undefined when it can't be derived. */
  relevantTopicsFor: (body: string) => string[] | undefined;
}

/**
 * Resolve a body's `[[topic]]` references against the org's topic catalog.
 *
 * Returns undefined — not [] — when the catalog isn't available, because the
 * request layer reads [] as "clear the stored list" and undefined as "leave it
 * unchanged". Without that distinction a save made while the catalog query was
 * erroring would wipe references the author never touched.
 *
 * That distinction is what lets the editors treat the catalog as optional
 * rather than a save prerequisite. Plenty of orgs have no normalized
 * topic-schema rows and never get a catalog at all, and for the largest orgs
 * that do, the endpoint behind it 504s (ENG-2818). Callers must not gate Save
 * on it: no `[[topic]]` highlighting is an acceptable degradation, a dead Save
 * button is not.
 */
export function useRelevantTopics(
  orgId: string | undefined,
  opts?: { enabled?: boolean },
): RelevantTopics {
  const topicNamesQuery = useOrgTopicNames(orgId, opts);
  const topicNames = React.useMemo(
    () => topicNamesQuery.data ?? [],
    [topicNamesQuery.data],
  );
  const isSuccess = topicNamesQuery.isSuccess;

  const relevantTopicsFor = React.useCallback(
    (body: string): string[] | undefined =>
      isSuccess ? extractTopicReferences(body, topicNames) : undefined,
    [isSuccess, topicNames],
  );

  return {
    topicNames,
    relevantTopicsFor,
  };
}
